// Phase 81vv — Live Matches section
// Phase 83y.21 — Use the shared `resolveCollectionAvatar` helper so users
// without an uploaded avatar render the same DiceBear avataaars seed
// they show everywhere else (consistent visual identity).
import { useEffect, useRef, useState } from "react";
import { Eye, Play, Broadcast } from "@phosphor-icons/react";
import { m as motion } from "framer-motion";
import { resolveCollectionAvatar } from "../../lib/avatarPool";
import OfflineAwareImage from "../common/OfflineAwareImage";

// Phase 83z.71 — `isRecap=true` khi không có trận live và section đang dùng
// chung kết gần nhất làm fallback. UI sẽ đổi heading + đổi badge LIVE → RECAP.
export default function LiveMatches({ matches = [], onSeeAll, onWatch, isRecap = false }) {
  const heading = isRecap ? "Chung kết gần nhất" : "Trận đấu đang LIVE";
  const iconColor = isRecap ? "#A8FF00" : "#FF8A00";
  // Phase 85.A.311 — Carousel CK: khi recap & nhiều trận CK cùng 1 giải.
  const isCarousel = isRecap && matches.length > 1;
  return (
    <section className="px-5 pt-7" data-testid="home-live-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span
            className="w-7 h-7 rounded-full border flex items-center justify-center"
            style={{
              backgroundColor: `${iconColor}26`,
              borderColor: `${iconColor}4D`,
            }}
          >
            <Broadcast size={14} weight="fill" style={{ color: iconColor }} />
          </span>
          <h2 className="text-[16px] sm:text-[17px] font-black tracking-wider text-white uppercase">
            {heading}
          </h2>
        </div>
        <button
          type="button"
          onClick={onSeeAll}
          className="text-[12px] font-semibold hover:opacity-80"
          style={{ color: iconColor }}
          data-testid="home-live-see-all"
        >
          Xem tất cả ›
        </button>
      </div>

      {matches.length === 0 ? (
        <div className="rounded-[24px] border border-dashed border-white/10 p-8 text-center text-[13px] text-[#94A3B8]" data-testid="home-live-empty">
          Hiện không có trận nào đang LIVE.
        </div>
      ) : isCarousel ? (
        <FinalsCarousel matches={matches} onWatch={onWatch} />
      ) : (
        <div className="space-y-3">
          {matches.slice(0, 1).map((m) => (
            <LiveMatchCard key={m.id} match={m} onWatch={() => onWatch?.(m)} isRecap={isRecap} />
          ))}
        </div>
      )}
    </section>
  );
}

// Phase 85.A.311 — Carousel CK: vuốt ngang (scroll-snap) + auto-play 5s + dots.
function FinalsCarousel({ matches = [], onWatch }) {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const pausedRef = useRef(false);

  const scrollTo = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = ((i % matches.length) + matches.length) % matches.length;
    const child = track.children[clamped];
    if (child) track.scrollTo({ left: child.offsetLeft - track.offsetLeft, behavior: "smooth" });
  };

  // Auto-play mỗi 5s (tạm dừng khi người dùng đang chạm/tương tác).
  useEffect(() => {
    if (matches.length <= 1) return undefined;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      setIndex((prev) => {
        const next = (prev + 1) % matches.length;
        scrollTo(next);
        return next;
      });
    }, 5000);
    return () => clearInterval(id);
  }, [matches.length]);

  // Đồng bộ dot theo vị trí scroll khi vuốt tay.
  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const center = track.scrollLeft + track.clientWidth / 2;
    let nearest = 0;
    let best = Infinity;
    Array.from(track.children).forEach((c, i) => {
      const mid = c.offsetLeft - track.offsetLeft + c.clientWidth / 2;
      const d = Math.abs(mid - center);
      if (d < best) { best = d; nearest = i; }
    });
    if (nearest !== index) setIndex(nearest);
  };

  return (
    <div data-testid="home-finals-carousel">
      <div
        ref={trackRef}
        onScroll={onScroll}
        onTouchStart={() => { pausedRef.current = true; }}
        onTouchEnd={() => { pausedRef.current = false; }}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-none -mx-1 px-1 pb-1"
        style={{ scrollbarWidth: "none" }}
      >
        {matches.map((m) => (
          <div key={m.id} className="snap-center shrink-0 w-[88%]">
            <LiveMatchCard match={m} onWatch={() => onWatch?.(m)} isRecap />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-1.5 mt-2.5" data-testid="home-finals-carousel-dots">
        {matches.map((m, i) => (
          <button
            key={m.id}
            type="button"
            onClick={() => { setIndex(i); scrollTo(i); }}
            className={`h-1.5 rounded-full transition-all ${i === index ? "w-5 bg-[#A8FF00]" : "w-1.5 bg-white/25"}`}
            data-testid={`home-finals-carousel-dot-${i}`}
            aria-label={`Trận CK ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

function LiveMatchCard({ match, onWatch, isRecap = false }) {
  const aLeading = match.scoreA > match.scoreB;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative overflow-hidden rounded-[24px] border border-white/8 bg-gradient-to-br from-[#111827]/95 to-[#0B1220]/85 backdrop-blur-xl"
      data-testid={`home-live-card-${match.id}`}
    >
      {isRecap ? (
        <div
          className="absolute top-3.5 right-4 flex items-center gap-1 px-2 py-0.5 rounded bg-[#1F8000] text-white text-[10px] font-black tracking-[0.18em]"
          data-testid="home-live-badge-recap"
        >
          RECAP · CK
        </div>
      ) : (
        <div className="absolute top-3.5 right-4 flex items-center gap-1 px-2 py-0.5 rounded bg-red-500/90 text-white text-[10px] font-black tracking-[0.18em]">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          LIVE
        </div>
      )}
      <div className="p-5 pt-4">
        <div className="text-[11px] text-[#94A3B8] font-medium tracking-wider mb-3">
          {match.eventName} · {match.round} · Sân {match.court}
        </div>
        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4">
          <div className="space-y-3">
            <PlayerRow players={match.teamA} leading={aLeading} />
            <PlayerRow players={match.teamB} leading={!aLeading} />
          </div>
          <div className="flex flex-col items-end gap-2 tabular-nums">
            <motion.div
              key={`a-${match.scoreA}`}
              initial={{ scale: 1.2, opacity: 0.6 }}
              animate={{ scale: 1, opacity: 1 }}
              className={`text-[44px] sm:text-[48px] font-black leading-none ${aLeading ? "text-[#FF8A00]" : "text-white/85"}`}
              data-testid={`home-live-card-${match.id}-scoreA`}
            >
              {match.scoreA}
            </motion.div>
            <motion.div
              key={`b-${match.scoreB}`}
              initial={{ scale: 1.2, opacity: 0.6 }}
              animate={{ scale: 1, opacity: 1 }}
              className={`text-[44px] sm:text-[48px] font-black leading-none ${!aLeading ? "text-[#FF8A00]" : "text-white/85"}`}
              data-testid={`home-live-card-${match.id}-scoreB`}
            >
              {match.scoreB}
            </motion.div>
          </div>
          <div className="flex flex-col items-center gap-2 ml-1.5">
            <button
              type="button"
              onClick={onWatch}
              className="w-10 h-10 rounded-full bg-[#FF8A00] flex items-center justify-center shadow-[0_0_18px_rgba(255,138,0,0.5)] active:scale-95 transition"
              data-testid={`home-live-card-${match.id}-watch`}
            >
              <Play size={16} weight="fill" className="text-white ml-0.5" />
            </button>
            <div className="text-center">
              <div className="text-[10px] text-[#94A3B8] font-medium">Game {match.game}</div>
              <div className="text-[12px] text-white font-bold tabular-nums">{match.gameScore}</div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-3 right-4 flex items-center gap-1 text-[#94A3B8] text-[11px]">
          <Eye size={12} weight="bold" />
          <span className="tabular-nums">{match.viewers}</span>
        </div>
      </div>
    </motion.div>
  );
}

function PlayerRow({ players = [], leading }) {
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <div className="flex -space-x-2 shrink-0">
        {players.slice(0, 2).map((p, i) => (
          <OfflineAwareImage
            key={i}
            src={p.avatar || resolveCollectionAvatar(p)}
            alt={p.name}
            width={32}
            height={32}
            className={`w-8 h-8 rounded-full border-2 ${leading ? "border-[#FF8A00]" : "border-white/15"} bg-[#0B1220]`}
          />
        ))}
      </div>
      <div className="min-w-0">
        {players.map((p, i) => (
          <div
            key={i}
            className={`text-[12.5px] font-semibold truncate leading-tight ${leading ? "text-white" : "text-[#94A3B8]"}`}
          >
            {p.name}
          </div>
        ))}
      </div>
    </div>
  );
}
